'use client';

import { Dialog, Transition } from '@headlessui/react';
import clsx from 'clsx';
import { Fragment } from 'react';
import { twMerge } from 'tailwind-merge';

import { ModalProvider } from './context';
import { ModalHeader } from './header';

import type { ModalProps } from './modal.types';

export const Modal = ({
  children,
  closeModal,
  description,
  expandHeader = false,
  heading,
  icon,
  isModalOpen = false,
  mobileSize = 'lg',
  openModal,
  testId = 'modal',
  headingStyle,
  iconStyle,
}: ModalProps) => (
  <ModalProvider
    value={{
      openModal,
      closeModal,
      isModalOpen,
    }}
  >
    <Transition
      appear
      show={isModalOpen}
      as={Fragment}
      data-testid={testId}
    >
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => null}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black opacity-[0.16]" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div
            className={twMerge(
              clsx(
                'flex h-full items-center justify-center text-center max-lg:pt-j',
                {
                  'absolute bottom-0 h-auto max-lg:pt-[20rem] sm:relative sm:h-full':
                    mobileSize === 'sm',
                }
              )
            )}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={twMerge(
                  clsx(
                    'flex h-full w-full flex-col rounded-b-none rounded-t-rad-b bg-white p-0 text-left transition-all lg:h-auto lg:max-h-[41rem] lg:max-w-[49.5rem] lg:rounded-rad-a',
                    {
                      'pb-c sm:p-0': mobileSize === 'sm',
                    }
                  )
                )}
              >
                <ModalHeader
                  icon={icon}
                  expandHeader={expandHeader}
                  heading={heading}
                  description={description}
                  headingStyle={headingStyle}
                  iconStyle={iconStyle}
                />
                {children}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  </ModalProvider>
);
